import React, { useEffect, useRef, useState } from "react";
import { authFetch } from "./auth";

const MAX_FILE_MB = 20;

class Unauthorized extends Error {}

export default function AdminGallery({ onLogout }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [title, setTitle] = useState("");
  const [kicker, setKicker] = useState("");
  const [creating, setCreating] = useState(false);

  // Llama a la API; ante 401 regresa al login, ante error lanza el mensaje del servidor.
  const api = async (url, options) => {
    const res = await authFetch(url, options);
    if (res.status === 401) {
      onLogout();
      throw new Unauthorized();
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Error");
    return data;
  };

  const sendJson = (url, method, body) =>
    api(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

  const run = async (fn, fallbackMsg) => {
    setError("");
    try {
      await fn();
    } catch (err) {
      if (err instanceof Unauthorized) return;
      setError(err.message && err.message !== "Error" ? err.message : fallbackMsg);
    }
  };

  const load = async () => {
    await run(async () => {
      const data = await api("/api/panel/gallery");
      setCategories(data.categories || []);
    }, "No se pudo cargar la galería");
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCategory = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setCreating(true);
    await run(async () => {
      await sendJson("/api/panel/gallery/categories", "POST", { title, kicker });
      setTitle("");
      setKicker("");
      await load();
    }, "No se pudo crear la categoría");
    setCreating(false);
  };

  // Mueve un elemento una posición y guarda el nuevo orden completo.
  const reorder = (type, list, index, dir, apply) => {
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    apply(next);
    run(
      () => sendJson("/api/panel/gallery/reorder", "POST", { type, ids: next.map((x) => x.Id) }),
      "No se pudo guardar el orden"
    ).then(() => load());
  };

  const moveCategory = (index, dir) => reorder("categories", categories, index, dir, setCategories);

  const moveImage = (cat, index, dir) =>
    reorder("images", cat.images, index, dir, (images) =>
      setCategories((prev) => prev.map((c) => (c.Id === cat.Id ? { ...c, images } : c)))
    );

  return (
    <div className="admin-grid">
      <div className="admin-side">
        <form className="admin-card" onSubmit={createCategory}>
          <h3>Nueva categoría</h3>

          <label className="admin-field">Título</label>
          <input
            className="admin-input"
            placeholder="Ej. Decoración"
            maxLength={80}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />

          <label className="admin-field">Subtítulo (opcional)</label>
          <input
            className="admin-input"
            placeholder="Ej. cada detalle cuenta"
            maxLength={120}
            value={kicker}
            onChange={(e) => setKicker(e.target.value)}
          />

          <button className="admin-btn" type="submit" disabled={creating || !title.trim()}>
            {creating ? "Creando…" : "Crear categoría"}
          </button>
        </form>

        <div className="admin-card admin-help">
          <h3>Cómo funciona</h3>
          <p>
            Cada foto que subes se ajusta sola: se gira según como se tomó, se reduce a máximo
            1280&nbsp;px y se comprime a JPG (~150&nbsp;KB), igual que las fotos actuales.
          </p>
          <p>
            Las categorías ocultas o sin fotos no aparecen en el sitio. Los cambios se ven en el
            sitio en cerca de un minuto.
          </p>
        </div>
      </div>

      <div className="admin-gal-list">
        {error && <div className="admin-error admin-error-top">{error}</div>}
        {loading && <div className="admin-card"><p className="admin-empty">Cargando…</p></div>}
        {!loading && categories.length === 0 && (
          <div className="admin-card">
            <p className="admin-empty">Todavía no hay categorías. Crea la primera a la izquierda.</p>
          </div>
        )}
        {categories.map((cat, i) => (
          <CategoryCard
            key={cat.Id}
            cat={cat}
            categories={categories}
            isFirst={i === 0}
            isLast={i === categories.length - 1}
            onMove={(dir) => moveCategory(i, dir)}
            onMoveImage={(idx, dir) => moveImage(cat, idx, dir)}
            api={api}
            sendJson={sendJson}
            run={run}
            reload={load}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryCard({ cat, categories, isFirst, isLast, onMove, onMoveImage, api, sendJson, run, reload }) {
  const [title, setTitle] = useState(cat.Title);
  const [kicker, setKicker] = useState(cat.Kicker || "");
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total } | null
  const fileRef = useRef(null);

  useEffect(() => {
    setTitle(cat.Title);
    setKicker(cat.Kicker || "");
  }, [cat.Title, cat.Kicker]);

  const dirty = title.trim() !== cat.Title || kicker.trim() !== (cat.Kicker || "");
  const visible = !!cat.IsActive;

  const save = async (isActive = visible) => {
    if (!title.trim()) return;
    setSaving(true);
    await run(async () => {
      await sendJson(`/api/panel/gallery/categories/${cat.Id}`, "PUT", { title, kicker, isActive });
      await reload();
    }, "No se pudo guardar la categoría");
    setSaving(false);
  };

  const remove = () => {
    if (!window.confirm(`¿Borrar la categoría "${cat.Title}"?`)) return;
    run(async () => {
      await api(`/api/panel/gallery/categories/${cat.Id}`, { method: "DELETE" });
      await reload();
    }, "No se pudo borrar la categoría");
  };

  // Se suben de una en una para no rebasar el límite de tamaño por petición.
  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const tooBig = files.filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    const ok = files.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024);

    setProgress({ done: 0, total: ok.length });
    const failed = tooBig.map((f) => `${f.name} (pesa más de ${MAX_FILE_MB} MB)`);
    for (let i = 0; i < ok.length; i++) {
      const form = new FormData();
      form.append("file", ok[i]);
      try {
        await api(`/api/panel/gallery/categories/${cat.Id}/images`, { method: "POST", body: form });
      } catch (err) {
        if (err instanceof Unauthorized) return;
        failed.push(`${ok[i].name} (${err.message})`);
      }
      setProgress({ done: i + 1, total: ok.length });
    }
    setProgress(null);
    if (fileRef.current) fileRef.current.value = "";
    await run(async () => {
      await reload();
      if (failed.length) throw new Error(`No se pudieron subir: ${failed.join(", ")}`);
    }, "No se pudo recargar la galería");
  };

  const removeImage = (img) => {
    if (!window.confirm("¿Borrar esta foto? No se puede deshacer.")) return;
    run(async () => {
      await api(`/api/panel/gallery/images/${img.Id}`, { method: "DELETE" });
      await reload();
    }, "No se pudo borrar la foto");
  };

  const moveToCategory = (img, categoryId) => {
    run(async () => {
      await sendJson(`/api/panel/gallery/images/${img.Id}`, "PUT", { categoryId: Number(categoryId) });
      await reload();
    }, "No se pudo mover la foto");
  };

  const others = categories.filter((c) => c.Id !== cat.Id);

  return (
    <div className={"admin-card admin-gal-cat" + (visible ? "" : " is-hidden")}>
      <div className="admin-gal-head">
        <div className="admin-gal-order">
          <button type="button" className="admin-icon-btn" disabled={isFirst} onClick={() => onMove(-1)} aria-label="Subir categoría">
            ▲
          </button>
          <button type="button" className="admin-icon-btn" disabled={isLast} onClick={() => onMove(1)} aria-label="Bajar categoría">
            ▼
          </button>
        </div>

        <div className="admin-gal-fields">
          <input className="admin-input" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} aria-label="Título" />
          <input
            className="admin-input"
            value={kicker}
            maxLength={120}
            placeholder="Subtítulo"
            onChange={(e) => setKicker(e.target.value)}
            aria-label="Subtítulo"
          />
        </div>

        <div className="admin-gal-actions">
          {dirty && (
            <button type="button" className="admin-btn admin-btn-sm" disabled={saving || !title.trim()} onClick={() => save()}>
              {saving ? "Guardando…" : "Guardar"}
            </button>
          )}
          <label className="admin-check">
            <input type="checkbox" checked={visible} disabled={saving} onChange={(e) => save(e.target.checked)} />
            Visible en el sitio
          </label>
          {cat.images.length === 0 && (
            <button type="button" className="admin-link-btn admin-danger" onClick={remove}>
              Borrar categoría
            </button>
          )}
        </div>
      </div>

      <div className="admin-thumbs">
        {cat.images.map((img, idx) => (
          <div key={img.Id} className="admin-thumb">
            <a href={img.Url} target="_blank" rel="noreferrer" className="admin-thumb-img" style={{ backgroundImage: `url(${img.Url})` }}>
              <span className="admin-thumb-pos">{idx + 1}</span>
            </a>
            <div className="admin-thumb-bar">
              <button type="button" className="admin-icon-btn" disabled={idx === 0} onClick={() => onMoveImage(idx, -1)} aria-label="Mover antes">
                ◀
              </button>
              <button
                type="button"
                className="admin-icon-btn"
                disabled={idx === cat.images.length - 1}
                onClick={() => onMoveImage(idx, 1)}
                aria-label="Mover después"
              >
                ▶
              </button>
              {others.length > 0 && (
                <select
                  className="admin-thumb-move"
                  value=""
                  onChange={(e) => e.target.value && moveToCategory(img, e.target.value)}
                  aria-label="Mover a otra categoría"
                >
                  <option value="">Mover a…</option>
                  {others.map((c) => (
                    <option key={c.Id} value={c.Id}>
                      {c.Title}
                    </option>
                  ))}
                </select>
              )}
              <button type="button" className="admin-icon-btn admin-danger" onClick={() => removeImage(img)} aria-label="Borrar foto">
                ✕
              </button>
            </div>
          </div>
        ))}

        <label className={"admin-thumb admin-upload" + (progress ? " busy" : "")}>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            disabled={!!progress}
            onChange={(e) => upload(e.target.files)}
          />
          <span className="admin-upload-plus">{progress ? "⏳" : "+"}</span>
          <span>{progress ? `Subiendo ${progress.done}/${progress.total}…` : "Agregar fotos"}</span>
        </label>
      </div>
    </div>
  );
}
